"use client";

import React, { useState, useEffect } from "react";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductSortDropdown, ProductsFilter, type EcommerceProduct } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Eye, SearchX, Loader, ShoppingCart } from "lucide-react";
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching
  } = useEcommerceProducts();
  const [selectedProduct, setSelectedProduct] = useState<EcommerceProduct | null>(null);
  if (loading && products.length === 0) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-[400px] animate-pulse rounded-xl border border-border/10 bg-card/50" />)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-destructive/20 bg-destructive/10 p-8 text-center">
        <p className="mb-4 text-destructive">Failed to load sneakers</p>
        <Button onClick={() => window.location.reload()} variant="outline" className="border-destructive/50 text-destructive hover:bg-destructive/20">
          Retry Connection
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-border/20 bg-card/30 py-24 text-center backdrop-blur-sm">
        <div className="mb-6 rounded-full bg-muted/20 p-5 ring-1 ring-border/30">
          <SearchX className="size-10 text-muted-foreground" />
        </div>
        <h3 className="mb-3 font-default text-2xl font-bold tracking-tight text-foreground">
          No Sneakers Found
        </h3>
        <p className="mb-8 max-w-md text-muted-foreground">
          Our scanners couldn't detect any sneaker matching your current
          parameters. Try adjusting your filters.
        </p>
        <Button onClick={() => window.location.reload()} variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 hover:text-primary">
          Reset Filters
        </Button>
      </div>;
  }
  return <div className="flex flex-col space-y-8">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => <article key={product.id} data-index={i} className="group relative flex flex-col overflow-hidden rounded-xl border border-border/30 bg-card/40 transition-all duration-300 hover:border-primary/50 hover:bg-card/80 hover:shadow-[0_0_20px_rgba(var(--primary),0.15)]">
            <div className="relative aspect-square overflow-hidden bg-muted/10">
              {product.featured_image_url ? <img src={product.featured_image_url} data-wvc-srcset={`${product.featured_image_url}-wvc-srcset`} data-wvc-sizes={`${product.featured_image_url}-wvc-sizes`} alt={product.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-muted/20">
                  <span className="text-muted-foreground">No Image</span>
                </div>}
              
              {}
              <div className="absolute inset-0 flex items-center justify-center bg-background/60 opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
                <Button onClick={e => {
              e.preventDefault();
              setSelectedProduct(product);
            }} variant="secondary" className="translate-y-4 shadow-lg transition-transform duration-300 group-hover:translate-y-0">
                  <Eye className="mr-2 size-4" />
                  Quick View
                </Button>
              </div>
            </div>

            <div className="flex flex-1 flex-col p-5">
              <Link to={product.link} className="block flex-1">
                <h3 className="font-default text-lg font-semibold tracking-tight text-foreground transition-colors group-hover:text-primary">
                  {product.title}
                </h3>
                {product.price !== undefined && <div className="mt-2 font-mono text-xl font-medium text-foreground/90">
                    ${product.price.toFixed(2)}
                  </div>}
              </Link>

              <div className="mt-5">
                <AddToCartButton productId={product.id} variant="outline" className="w-full" buttonClassName="w-full border-border/50 bg-transparent text-foreground hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors" text="Add to Cart" showQuantity={false} />
              </div>
            </div>
          </article>)}
      </div>

      {hasNext && <div className="flex justify-center pt-8">
          <Button onClick={async () => await loadMore()} disabled={isRefetching} variant="outline" size="lg" className="min-w-[200px] border-primary/30 text-primary hover:bg-primary/10 hover:text-primary">
            {isRefetching ? <>
                <Loader className="mr-2 size-4 animate-spin" />
                Scanning...
              </> : "Load More Frequencies"}
          </Button>
        </div>}

      {}
      <Dialog open={!!selectedProduct} onOpenChange={open => !open && setSelectedProduct(null)}>
        <DialogContent className="border-border/40 bg-background/95 p-0 sm:max-w-[800px] overflow-hidden backdrop-blur-xl">
          {selectedProduct && <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="relative aspect-square md:aspect-auto bg-muted/10">
                {selectedProduct.featured_image_url && <img src={selectedProduct.featured_image_url} data-wvc-srcset={`${selectedProduct.featured_image_url}-wvc-srcset`} data-wvc-sizes={`${selectedProduct.featured_image_url}-wvc-sizes`} alt={selectedProduct.title} className="absolute inset-0 h-full w-full object-cover" />}
              </div>
              <div className="flex flex-col justify-center p-8">
                <DialogHeader className="mb-6 text-left">
                  <DialogTitle className="font-default text-3xl font-bold tracking-tight">
                    {selectedProduct.title}
                  </DialogTitle>
                </DialogHeader>

                {selectedProduct.price !== undefined && <div className="mb-6 font-mono text-3xl font-medium text-primary">
                    ${selectedProduct.price.toFixed(2)}
                  </div>}

                {selectedProduct.short_description && <div className="prose prose-invert prose-sm mb-8 text-muted-foreground [&_p]:mb-2 [&_p:last-child]:mb-0" dangerouslySetInnerHTML={{
              __html: selectedProduct.short_description
            }} />}

                <div className="mt-auto space-y-4">
                  <AddToCartButton productId={selectedProduct.id} className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-lg font-medium shadow-[0_0_15px_rgba(var(--primary),0.3)] transition-all hover:shadow-[0_0_25px_rgba(var(--primary),0.5)]" text="Add to Cart" showQuantity={true} />
                  <Button asChild variant="link" className="w-full text-muted-foreground hover:text-primary">
                    <Link to={selectedProduct.link}>View Full Specs →</Link>
                  </Button>
                </div>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
    </div>;
}
export default function ShopContent() {
  const [wpQuery, setWpQuery] = useState<WP_Query | null>(null);
  useEffect(() => {
    if (typeof window !== "undefined" && (window as any).wvcClient) {
      const currentParams = (window as any).wvcClient.getCurrentWPQueryParams();
      setWpQuery(new WP_Query(currentParams));
    } else {
      setWpQuery(new WP_Query({
        post_type: "product",
        posts_per_page: 9
      }));
    }
  }, []);
  if (!wpQuery) {
    return <section id="shop-content" className="min-h-screen bg-background text-foreground py-16 px-4 md:px-8">
        <div className="container mx-auto flex items-center justify-center">
          <Loader className="size-8 animate-spin text-primary" />
        </div>
      </section>;
  }
  return <section id="shop-content" className="min-h-screen bg-background text-foreground py-16 px-4 md:px-8 lg:px-12">
      <div className="container mx-auto max-w-[1400px]">
        <EcommerceProductsProvider wp_query={wpQuery}>
          <div className="flex flex-col gap-8 lg:flex-row lg:gap-12">
            {}
            <aside className="w-full shrink-0 lg:sticky lg:top-24 lg:h-[calc(100vh-8rem)] lg:w-64 lg:overflow-y-auto xl:w-72 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-muted/50">
              <div className="rounded-xl border border-border/20 bg-card/20 p-6 backdrop-blur-md">
                <h2 className="mb-6 font-default text-xl font-bold tracking-tight">
                  Choose your sneaker
                </h2>
                <ProductsFilter />
              </div>
            </aside>

            {}
            <div className="flex w-full flex-col space-y-6 lg:w-auto lg:flex-1">
              <div className="flex flex-col items-start justify-between gap-4 rounded-xl border border-border/20 bg-card/20 p-4 backdrop-blur-md sm:flex-row sm:items-center">
                <p className="text-sm text-muted-foreground">
                  Displaying available sneakers 
                </p>
                <div className="w-full sm:w-auto">
                  <ProductSortDropdown label="Sort Sneakers" />
                </div>
              </div>

              <ProductsGridConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>;
}